import Bugsnag from '@bugsnag/js'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { confirmCoherence, correctCoherence } from '../../services/iperiaStatus'
import type { IperiaCoherenceAlert } from '../../types/iperiaStatus'
import {
  coherenceErrorMessage,
  coherenceInputType,
  formatCoherenceValue,
  groupCoherenceAlerts,
  isConfirmable,
} from '../../utils/iperiaCoherence'

type Props = {
  globalId: string
  alerts: IperiaCoherenceAlert[]
  intro: string
  /** Recharge le statut : le bulletin se prépare ensuite, la page de signature prend le relais. */
  onDone: () => Promise<void>
}

const CoherenceField = ({
  globalId,
  alert,
  showLabel,
  disabled,
  onDone,
}: {
  globalId: string
  alert: IperiaCoherenceAlert
  /** Plusieurs champs dans la carte : chacun dit ce qu'il corrige. */
  showLabel: boolean
  disabled: boolean
  onDone: () => Promise<void>
}) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(alert.value)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await correctCoherence(globalId, alert.code, value)
      setEditing(false)
      await onDone()
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status !== 400) Bugsnag.notify(err as Error)
      setError(coherenceErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const editable = alert.editable
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {alert.field && (
        <Typography variant="body2" color="text.secondary">
          {showLabel ? `${alert.label} : ` : 'Information enregistrée : '}
          <strong>{formatCoherenceValue(alert)}</strong>
        </Typography>
      )}
      {editable && !editing && (
        <Button
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          disabled={disabled}
          onClick={() => {
            setValue(alert.value)
            setEditing(true)
          }}
        >
          {showLabel ? `Corriger ${alert.label.charAt(0).toLowerCase()}${alert.label.slice(1)}` : 'Corriger'}
        </Button>
      )}
      {editable && editing && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            label={alert.label}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            type={coherenceInputType(editable.kind)}
            select={editable.kind === 'choice'}
            InputLabelProps={editable.kind === 'date' ? { shrink: true } : undefined}
            error={!!error}
            helperText={error ?? ' '}
            disabled={saving}
            fullWidth
            autoFocus
          >
            {editable.kind === 'choice' &&
              editable.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => void save()} disabled={saving || !value.trim()}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer la correction'}
            </Button>
            <Button
              onClick={() => {
                setEditing(false)
                setError(null)
              }}
              disabled={saving}
            >
              Annuler
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

const CoherenceAlertCard = ({
  globalId,
  alerts,
  disabled,
  onDone,
}: {
  globalId: string
  alerts: IperiaCoherenceAlert[]
  disabled: boolean
  onDone: () => Promise<void>
}) => {
  const [first] = alerts
  const several = alerts.length > 1
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderColor: isConfirmable(first) ? undefined : 'error.main',
      }}
    >
      {(!several || !isConfirmable(first)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {!several && <Typography sx={{ fontWeight: 600 }}>{first.label}</Typography>}
          {!isConfirmable(first) && <Chip size="small" color="error" label="À corriger" />}
        </Box>
      )}
      <Typography>{first.message}</Typography>
      {alerts.map((alert) => (
        <CoherenceField
          key={alert.code}
          globalId={globalId}
          alert={alert}
          showLabel={several}
          disabled={disabled}
          onDone={onDone}
        />
      ))}
    </Paper>
  )
}

/**
 * Inscription autonome : avant de préparer le bulletin, l'AM voit ce qui semble ne pas
 * correspondre dans son dossier. Elle corrige, ou confirme et continue : un doute ne la bloque
 * pas, le dossier est alors revu par l'équipe Edumiam. Une erreur certaine (« À corriger ») se
 * corrige avant de continuer.
 */
const IperiaCoherenceAlerts = ({ globalId, alerts, intro, onDone }: Props) => {
  const [confirming, setConfirming] = useState(false)
  const [confirmFailed, setConfirmFailed] = useState(false)
  const toCorrect = alerts.some((alert) => !isConfirmable(alert))

  const confirm = async () => {
    setConfirming(true)
    setConfirmFailed(false)
    try {
      await confirmCoherence(
        globalId,
        alerts.filter(isConfirmable).map((alert) => alert.code),
      )
    } catch (err) {
      Bugsnag.notify(err as Error)
      setConfirmFailed(true)
    }
    try {
      await onDone()
    } finally {
      setConfirming(false)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity={toCorrect ? 'error' : 'warning'}>
        <AlertTitle>
          {toCorrect ? 'Corrigez ces informations avant de signer' : 'Vérifiez ces informations avant de signer'}
        </AlertTitle>
        {intro}
      </Alert>
      {groupCoherenceAlerts(alerts).map((group) => (
        <CoherenceAlertCard
          key={group[0].code}
          globalId={globalId}
          alerts={group}
          disabled={confirming}
          onDone={onDone}
        />
      ))}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
        {toCorrect ? (
          <Typography>
            Les informations marquées « À corriger » ne peuvent pas être exactes telles qu'elles sont saisies :
            corrigez-les pour continuer.
          </Typography>
        ) : (
          <>
            <Typography>
              Tout est exact ? Continuez : votre bulletin sera préparé avec ces informations, et notre équipe les
              vérifiera de son côté.
            </Typography>
            {confirmFailed && (
              <Alert severity="info">
                Votre réponse n'a pas pu être envoyée. Vérifiez votre connexion, puis cliquez à nouveau sur le bouton.
              </Alert>
            )}
            <Button
              variant="contained"
              sx={{ alignSelf: 'flex-start' }}
              disabled={confirming}
              onClick={() => void confirm()}
            >
              {confirming ? <CircularProgress size={20} color="inherit" /> : 'Mes informations sont exactes, continuer'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  )
}

export default IperiaCoherenceAlerts
